import { useEffect, useState } from "react";
import axios from "axios";
import { getAuthHeader } from "../context/AuthContext";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

import {
  Loader2,
  MapPin,
  Phone,
  ShoppingBag,
  Clock3,
  Bike,
  CheckCircle,
  LogOut,
  Package,
} from "lucide-react";

import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function RiderPanelPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState("");
  const [tab, setTab] = useState("active");

  useEffect(() => {
    fetchOrders();

    const interval = setInterval(fetchOrders, 10000);
    return () => clearInterval(interval);
  }, [tab]);

  const fetchOrders = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API}/delivery/my-orders`,
        {
          headers: getAuthHeader(),
          params: { tab },
        }
      );

      setOrders(response.data.data || []);
    } catch (error) {
      toast.error("Failed to load deliveries");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("staff_token");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.location.href = "/login";
  };

  const updateStatus = async (
    orderId,
    status
  ) => {
    try {
      setUpdatingId(orderId + status);

      await axios.post(
        `${API}/delivery/update-status`,
        {
          order_id: orderId,
          status,
        },
        {
          headers: getAuthHeader(),
        }
      );

      toast.success("Updated");
      fetchOrders();
    } catch {
      toast.error("Failed");
    } finally {
      setUpdatingId("");
    }
  };

  const renderButton = (order) => {
    if (tab !== "active") return null;

    if (order.delivery_status === "assigned")
      return (
        <Button
          className="w-full"
          onClick={() =>
            updateStatus(
              order.id,
              "picked_up"
            )
          }
        >
          <ShoppingBag className="mr-2 h-4 w-4" />
          Pickup
        </Button>
      );

    if (
      order.delivery_status ===
      "picked_up"
    )
      return (
        <Button
          className="w-full"
          onClick={() =>
            updateStatus(
              order.id,
              "out_for_delivery"
            )
          }
        >
          <Bike className="mr-2 h-4 w-4" />
          Start Delivery
        </Button>
      );

    if (
      order.delivery_status ===
      "out_for_delivery"
    )
      return (
        <Button
          className="w-full bg-green-600 hover:bg-green-700"
          onClick={() =>
            updateStatus(
              order.id,
              "delivered"
            )
          }
        >
          <CheckCircle className="mr-2 h-4 w-4" />
          Delivered
        </Button>
      );

    return null;
  };

  const formatStatus = (status) =>
    status
      ?.replaceAll("_", " ")
      ?.replace(/\b\w/g, (c) =>
        c.toUpperCase()
      );

  const TabButton = ({
    id,
    label,
  }) => (
    <button
      onClick={() => setTab(id)}
      className={`px-4 py-2 rounded-xl text-sm font-medium ${
        tab === id
          ? "bg-black text-white"
          : "bg-white border"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-100 p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">
            Rider Dashboard
          </h1>
          <p className="text-slate-500">
            Manage deliveries
          </p>
        </div>

        <Button
          variant="outline"
          onClick={logout}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Logout
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-3 mb-6">
        <TabButton
          id="active"
          label="Assigned Orders"
        />
        <TabButton
          id="completed"
          label="Completed"
        />
        {/* <TabButton
          id="cancelled"
          label="Cancelled"
        /> */}
        <TabButton
          id="all"
          label="All Orders"
        />
      </div>

      {/* Stats */}
      <div className="mb-6">
        <Card>
          <CardContent className="p-5">
            <div className="text-sm text-slate-500">
              Total Orders
            </div>
            <div className="text-3xl font-bold mt-2">
              {orders.length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex gap-2 items-center">
          <Loader2 className="animate-spin" />
          Loading...
        </div>
      ) : (
        <div className="space-y-5">
          {orders.map((order) => (
            <Card
              key={order.id}
              className="rounded-2xl shadow-sm border-0"
            >
              <CardContent className="p-5">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex gap-2 items-center mb-3">
                      <Package size={18} />
                      <h2 className="font-bold">
                        #
                        {order.id.slice(
                          0,
                          8
                        )}
                      </h2>
                    </div>

                    <p className="font-semibold">
                      {
                        order.customer_name
                      }
                    </p>

                    <div className="mt-3 text-sm text-slate-500 space-y-2">
                      <div className="flex gap-2">
                        <Phone size={14} />
                        {
                          order.customer_phone
                        }
                      </div>

                      <div className="flex gap-2">
                        <MapPin size={14} />
                        {
                          order.customer_address
                        }
                      </div>

                      <div className="flex gap-2">
                        <Clock3 size={14} />
                        {new Date(
                          order.created_at
                        ).toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-slate-500">
                      Delivery Status
                    </p>

                    <p className="font-bold text-lg mb-4">
                      {formatStatus(
                        order.delivery_status
                      )}
                    </p>

                    <p className="mb-4 font-semibold">
                      ₹
                      {
                        order.grand_total
                      }
                    </p>

                    {renderButton(order)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {orders.length === 0 && (
            <Card>
              <CardContent className="p-10 text-center text-slate-500">
                No orders found
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}